import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '../components/SiteNavbar';
import SiteFooter from '../components/SiteFooter';
import LineIcon from '../components/LineIcon';
import Reveal from '../components/Reveal';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const LIGHT_GRAY = '#F7F7F7';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const CALENDLY_URL = 'https://calendly.com/alexis-vocationworks/30min';

// marketing.tradesPlural keys, resolved via t() at render time (see LandingPage.jsx for the
// same pattern) -- this module-level constant has no hook access.
const TRADES = [
  'electricians', 'plumbers', 'welders', 'hvacTechnicians', 'carpenters',
  'pipefitters', 'ironworkers', 'sheetMetalWorkers', 'millwrights',
  'industrialMaintenance', 'cncOperators', 'machineOperators',
  'dieselMechanics', 'generalLabor',
];

// title/desc/bullet keys ref marketing.union.*, resolved via t() at render time.
const CHALLENGES = [
  { icon: 'users', titleKey: 'challenge1Title', descKey: 'challenge1Desc' },
  { icon: 'trendingDown', titleKey: 'challenge2Title', descKey: 'challenge2Desc' },
  { icon: 'globe', titleKey: 'challenge3Title', descKey: 'challenge3Desc' },
];

const BENEFITS = [
  { num: '01', titleKey: 'benefit1Title', descKey: 'benefit1Desc', bulletKeys: ['benefit1Bullet1', 'benefit1Bullet2', 'benefit1Bullet3'] },
  { num: '02', titleKey: 'benefit2Title', descKey: 'benefit2Desc', bulletKeys: ['benefit2Bullet1', 'benefit2Bullet2', 'benefit2Bullet3'] },
  { num: '03', titleKey: 'benefit3Title', descKey: 'benefit3Desc', bulletKeys: ['benefit3Bullet1', 'benefit3Bullet2', 'benefit3Bullet3'] },
];

const PARTNER_PERKS = [
  { icon: 'tag', titleKey: 'perk1Title', descKey: 'perk1Desc' },
  { icon: 'barChart', titleKey: 'perk2Title', descKey: 'perk2Desc' },
  { icon: 'megaphone', titleKey: 'perk3Title', descKey: 'perk3Desc' },
  { icon: 'graduation', titleKey: 'perk4Title', descKey: 'perk4Desc' },
  { icon: 'hardHat', titleKey: 'perk5Title', descKey: 'perk5Desc' },
  { icon: 'handshake', titleKey: 'perk6Title', descKey: 'perk6Desc' },
];

const scrollToHowItWorks = () => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });

export default function UnionPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { background-color: ${NAVY}; }
      @media (max-width: 768px) {
        .un-nav            { padding: 0 1.25rem !important; }
        .un-nav-links      { display: none !important; }
        .un-nav-join-union { display: none !important; }
        .un-headline       { font-size: 42px !important; }
        .un-hero-ctas      { flex-direction: column !important; align-items: stretch !important; }
        .un-hero-ctas > *  { width: 100% !important; justify-content: center; text-align: center; }
        .un-challenge-grid { grid-template-columns: 1fr !important; }
        .un-trades-grid    { gap: 0.5rem !important; }
        .un-partner-grid   { grid-template-columns: 1fr !important; }
        .un-final-cta      { padding: 3.5rem 1.5rem !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ fontFamily: FONT_BODY, color: WHITE }}>

      {/* ── 1. NAVBAR ── */}
      <SiteNavbar
        activePage="union"
        secondaryCta={{ label: t('marketing.union.joinAsUnion'), onClick: () => navigate('/union/signup/step1'), variant: 'white' }}
        primaryCta={{ label: t('marketing.union.bookDemo'), href: CALENDLY_URL }}
      />

      {/* ── 2. HERO ── */}
      <section className="un-hero" style={{
        backgroundColor: NAVY, padding: '120px 2rem 80px',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
        borderBottom: `1.5px solid ${AMBER}`,
      }}>
        <div style={{
          position: 'absolute', top: '40%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px', height: '460px',
          background: 'radial-gradient(ellipse, rgba(245,166,35,0.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            backgroundColor: 'rgba(245,166,35,0.12)',
            border: '1px solid rgba(245,166,35,0.35)',
            borderRadius: '999px', padding: '7px 18px',
            fontSize: '0.8rem', fontWeight: 600, color: AMBER,
            marginBottom: '1.75rem', letterSpacing: '0.04em',
          }}>
            {t('marketing.union.badge')}
          </div>

          <h1 className="un-headline" style={{
            fontFamily: FONT_HEAD,
            fontSize: 'clamp(2.75rem, 6.5vw, 5rem)',
            color: WHITE, letterSpacing: '0.02em',
            lineHeight: 1.05, marginBottom: '1.5rem',
          }}>
            {t('marketing.union.heroTitle')}
          </h1>

          <p style={{
            fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
            color: 'rgba(255,255,255,0.75)',
            marginBottom: '2.75rem', lineHeight: 1.65,
            maxWidth: '680px', margin: '0 auto 2.75rem',
          }}>
            {t('marketing.union.heroSubtitle')}
          </p>

          <div className="un-hero-ctas" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: AMBER, color: NAVY, border: 'none',
                borderRadius: '8px', padding: '16px 34px',
                fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {t('marketing.union.bookDemo')}
            </a>
            <button
              onClick={scrollToHowItWorks}
              style={{
                backgroundColor: 'transparent', color: WHITE,
                border: '2px solid rgba(255,255,255,0.35)',
                borderRadius: '8px', padding: '16px 34px',
                fontWeight: 600, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
              }}
            >
              {t('marketing.union.learnMoreArrow')}
            </button>
          </div>
        </div>
      </section>

      {/* ── 4. THE CHALLENGE ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal>
            <h2 style={{
              fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '1.25rem',
            }}>
              {t('marketing.union.challengeTitle')}
            </h2>
            <div style={{ width: '56px', height: '3px', backgroundColor: AMBER, margin: '0 auto 2rem' }} />
            <p style={{
              fontSize: '1rem', color: '#555', lineHeight: 1.8,
              maxWidth: '760px', margin: '0 auto 3.5rem', textAlign: 'center',
            }}>
              {t('marketing.union.challengeBody')}
            </p>
          </Reveal>

          <div className="un-challenge-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {CHALLENGES.map((c, i) => (
              <Reveal stagger key={i} style={{
                backgroundColor: '#F8F8F8',
                borderTop: `3px solid ${AMBER}`,
                borderRadius: '10px',
                padding: '2rem',
              }}>
                <div style={{ marginBottom: '1rem' }}><LineIcon name={c.icon} size={30}  color={AMBER} /></div>
                <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: NAVY, marginBottom: '0.75rem' }}>
                  {t(`marketing.union.${c.titleKey}`)}
                </h3>
                <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.7 }}>
                  {t(`marketing.union.${c.descKey}`)}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. HOW VOCATION WORKS SUPPORTS UNIONS ── */}
      <section id="how-it-works" style={{ backgroundColor: LIGHT_GRAY, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '4rem',
          }}>
            {t('marketing.union.howWeSupportTitle')}
          </Reveal>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4.5rem' }}>
            {BENEFITS.map((b) => (
              <Reveal key={b.num} style={{ width: '100%' }}>
                <div style={{
                  fontFamily: FONT_HEAD, fontSize: '3rem', color: AMBER,
                  letterSpacing: '0.02em', lineHeight: 1, marginBottom: '0.5rem',
                }}>
                  {b.num}
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1.5rem', color: NAVY, marginBottom: '1rem', lineHeight: 1.3 }}>
                  {t(`marketing.union.${b.titleKey}`)}
                </h3>
                <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: 1.75, marginBottom: '1.5rem' }}>
                  {t(`marketing.union.${b.descKey}`)}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {b.bulletKeys.map((bulletKey, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{ color: AMBER, fontWeight: 800, fontSize: '1rem', lineHeight: 1.5 }}>●</span>
                      <span style={{ fontSize: '0.9rem', color: NAVY, fontWeight: 500, lineHeight: 1.5 }}>{t(`marketing.union.${bulletKey}`)}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}

            {/* Short card: published union wage scales on the Union Wages pages. */}
            <Reveal data-wage-scale-card style={{
              backgroundColor: WHITE, borderTop: `3px solid ${AMBER}`, borderRadius: '12px',
              boxShadow: '0 2px 16px rgba(13,27,42,0.08)', padding: '1.75rem 2rem',
              display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap',
            }}>
              <LineIcon name="dollar" size={30}  color={AMBER} />
              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                <h3 style={{ fontWeight: 700, fontSize: '1.2rem', color: NAVY, marginBottom: '0.6rem', lineHeight: 1.3 }}>
                  {t('marketing.union.wageScaleTitle')}
                </h3>
                <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: 1.7, marginBottom: '0.5rem' }}>
                  {t('marketing.union.wageScaleDesc')}
                </p>
                <Link to="/union-wages" style={{
                  display: 'inline-flex', alignItems: 'center', minHeight: '44px', color: NAVY, fontWeight: 700,
                  fontSize: '0.95rem', textDecoration: 'underline', textUnderlineOffset: '3px', textDecorationColor: AMBER,
                }}>
                  {t('marketing.union.wageScaleLink')}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 6. WHO WE SERVE ── */}
      <section style={{ backgroundColor: NAVY, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <Reveal>
            <h2 style={{
              fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              color: AMBER, letterSpacing: '0.03em', marginBottom: '1rem',
            }}>
              {t('marketing.union.tradesWeServeTitle')}
            </h2>
            <p style={{ fontSize: '0.975rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, marginBottom: '2.5rem' }}>
              {t('marketing.union.tradesWeServeSubtitle')}
            </p>
          </Reveal>

          <Reveal className="un-trades-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
            {TRADES.map((tradeKey, i) => (
              <span key={i} style={{
                padding: '9px 20px', borderRadius: '999px',
                backgroundColor: i % 2 === 0 ? AMBER : NAVY,
                color: i % 2 === 0 ? NAVY : WHITE,
                border: i % 2 === 0 ? 'none' : `1px solid rgba(245,166,35,0.4)`,
                fontWeight: 600, fontSize: '0.875rem',
              }}>
                {t(`marketing.tradesPlural.${tradeKey}`)}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── 7. PARTNERSHIP BENEFITS ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '3.5rem',
          }}>
            {t('marketing.union.partnerBenefitsTitle')}
          </Reveal>

          <div className="un-partner-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {PARTNER_PERKS.map((p, i) => (
              <Reveal stagger key={i} style={{
                backgroundColor: WHITE,
                borderRadius: '12px',
                borderTop: `3px solid ${AMBER}`,
                boxShadow: '0 2px 16px rgba(13,27,42,0.08)',
                padding: '2rem',
              }}>
                <div style={{ marginBottom: '1rem' }}><LineIcon name={p.icon} size={30}  color={AMBER} /></div>
                <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: NAVY, marginBottom: '0.75rem' }}>
                  {t(`marketing.union.${p.titleKey}`)}
                </h3>
                <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.7 }}>
                  {t(`marketing.union.${p.descKey}`)}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. FINAL CTA ── */}
      <section className="un-final-cta" style={{ backgroundColor: AMBER, padding: '5rem 2rem', textAlign: 'center' }}>
        <Reveal style={{ maxWidth: '640px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.5rem, 6vw, 4rem)',
            color: NAVY, letterSpacing: '0.03em', marginBottom: '1.25rem',
          }}>
            {t('marketing.union.finalCtaTitle')}
          </h2>
          <p style={{
            fontSize: '1.05rem', color: 'rgba(13,27,42,0.78)',
            lineHeight: 1.7, marginBottom: '2.25rem', fontWeight: 500,
          }}>
            {t('marketing.union.finalCtaBody')}
          </p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              backgroundColor: NAVY, color: WHITE,
              border: 'none', borderRadius: '8px', padding: '18px 40px',
              fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer', fontFamily: FONT_BODY,
              textDecoration: 'none', marginBottom: '1.25rem',
            }}
          >
            {t('marketing.union.bookFreeDemo')}
          </a>
          <p style={{ fontSize: '0.875rem', color: 'rgba(13,27,42,0.65)' }}>
            {t('marketing.union.questionsContact')}
          </p>
        </Reveal>
      </section>

      {/* ── 9. FOOTER ── */}
      <SiteFooter />

    </div>
  );
}
